import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { tendersService } from '../services/tendersService'
import { formatDate, formatDateTime } from '../utils/helpers'

/**
 * TenderDetail — Admin sub-page for viewing, editing, and deleting a single tender.
 */
export default function TenderDetail() {
    const { id } = useParams()
    const navigate = useNavigate()

    const [tender, setTender] = useState(null)
    const [savedByCount, setSavedByCount] = useState(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    // Edit mode
    const [editing, setEditing] = useState(false)
    const [form, setForm] = useState({})
    const [saving, setSaving] = useState(false)

    // Delete confirmation
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [deleting, setDeleting] = useState(false)

    const fetchTender = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const result = await tendersService.getById(id)
            setTender(result.tender)
            setSavedByCount(result.savedByCount || 0)
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }, [id])

    useEffect(() => {
        fetchTender()
    }, [fetchTender])

    // Populate form when entering edit mode
    const startEditing = () => {
        if (!tender) return
        setForm({
            title: tender.title || '',
            description: tender.description || '',
            buyer: tender.buyer || tender.buyer_name || '',
            nation: tender.nation || '',
            region: tender.region || '',
            source: tender.source || tender.source_platform || '',
            noticeType: tender.noticeType || tender.notice_type || '',
            estimatedValue: tender.estimatedValue || tender.estimated_value || '',
            currency: tender.currency || 'GBP',
            deadline: tender.deadline || tender.closing_date || '',
            publishedDate: tender.publishedDate || tender.published_date || '',
            startDate: tender.startDate || tender.start_date || '',
            noticeUrl: tender.noticeUrl || tender.notice_url || '',
            contactEmail: tender.contactEmail || tender.contact_email || '',
            awardCriteria: tender.awardCriteria || tender.award_criteria || '',
            fullDescription: tender.fullDescription || tender.full_description || '',
        })
        setEditing(true)
    }

    const cancelEditing = () => {
        setEditing(false)
        setForm({})
    }

    const handleFieldChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }))
    }

    const handleSave = async () => {
        try {
            setSaving(true)
            await tendersService.update(id, form)
            await fetchTender()
            setEditing(false)
        } catch (err) {
            alert('Failed to save: ' + err.message)
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        try {
            setDeleting(true)
            await tendersService.deleteTender(id)
            navigate('/tenders', { replace: true })
        } catch (err) {
            alert('Failed to delete: ' + err.message)
            setDeleting(false)
        }
    }

    // Derive status
    const getStatus = () => {
        if (!tender) return 'unknown'
        const deadline = tender.closing_date || tender.deadline
        if (deadline && new Date(deadline) < new Date()) return 'expired'
        return 'open'
    }

    if (loading) {
        return (
            <div className="card-admin p-12 text-center text-admin-muted font-bold animate-fade-in">
                <div className="flex items-center justify-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent-primary border-t-transparent" />
                    Loading tender details...
                </div>
            </div>
        )
    }

    if (error || !tender) {
        return (
            <div className="space-y-4 animate-fade-in">
                <Link to="/tenders" className="btn-secondary btn-sm">
                    ← Back to Tenders
                </Link>
                <div className="p-4 bg-status-error-bg border border-status-error/20 text-status-error text-sm font-bold rounded-lg">
                    {error || 'Tender not found.'}
                </div>
            </div>
        )
    }

    const status = getStatus()
    const displayId = tender.external_ref_id || tender.noticeId || tender.id

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex items-start gap-4 min-w-0 flex-1">
                    <Link to="/tenders" className="btn-secondary btn-sm mt-1 shrink-0">
                        ← Back
                    </Link>
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-3">
                            <h1 className="text-xl font-bold text-admin-heading tracking-tight leading-snug">
                                {tender.title}
                            </h1>
                            <TenderStatusBadge status={status} />
                        </div>
                        <p className="text-sm text-admin-muted font-medium mt-1 font-mono truncate">
                            {displayId}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button onClick={fetchTender} className="btn-secondary shadow-sm">
                        Refresh
                    </button>
                    {!editing && (
                        <button onClick={startEditing} className="btn-primary shadow-sm">
                            Edit Tender
                        </button>
                    )}
                </div>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard label="Source Platform" value={tender.source_platform || tender.source || '—'} />
                <MetricCard
                    label="Nation / Region"
                    value={[tender.nation, tender.region ? `(${tender.region})` : null].filter(Boolean).join(' ') || '—'}
                />
                <MetricCard
                    label="Deadline"
                    value={formatDate(tender.closing_date || tender.deadline)}
                    highlight={status === 'open'}
                />
                <MetricCard label="Saved By Orgs" value={savedByCount} />
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* Left Column — Details */}
                <div className="xl:col-span-2 space-y-6">
                    {/* Core Details */}
                    <section className="card-admin">
                        <div className="card-header flex items-center justify-between">
                            <h2 className="text-sm font-bold text-admin-heading uppercase tracking-wide">Tender Details</h2>
                            {editing && (
                                <div className="flex items-center gap-2">
                                    <button onClick={cancelEditing} className="btn-secondary btn-sm">
                                        Cancel
                                    </button>
                                    <button onClick={handleSave} disabled={saving} className="btn-primary btn-sm">
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4">
                            {editing ? (
                                <>
                                    <EditField label="Title" value={form.title} onChange={(v) => handleFieldChange('title', v)} span2 />
                                    <EditField label="Buyer" value={form.buyer} onChange={(v) => handleFieldChange('buyer', v)} />
                                    <EditField label="Source" value={form.source} onChange={(v) => handleFieldChange('source', v)} />
                                    <EditField label="Nation" value={form.nation} onChange={(v) => handleFieldChange('nation', v)} />
                                    <EditField label="Region" value={form.region} onChange={(v) => handleFieldChange('region', v)} />
                                    <EditField label="Notice Type" value={form.noticeType} onChange={(v) => handleFieldChange('noticeType', v)} />
                                    <EditField label="Estimated Value" value={form.estimatedValue} onChange={(v) => handleFieldChange('estimatedValue', v)} />
                                    <EditField label="Currency" value={form.currency} onChange={(v) => handleFieldChange('currency', v)} />
                                    <EditField label="Published Date" value={form.publishedDate} onChange={(v) => handleFieldChange('publishedDate', v)} type="date" />
                                    <EditField label="Deadline" value={form.deadline} onChange={(v) => handleFieldChange('deadline', v)} type="date" />
                                    <EditField label="Start Date" value={form.startDate} onChange={(v) => handleFieldChange('startDate', v)} type="date" />
                                    <EditField label="Notice URL" value={form.noticeUrl} onChange={(v) => handleFieldChange('noticeUrl', v)} />
                                    <EditField label="Contact Email" value={form.contactEmail} onChange={(v) => handleFieldChange('contactEmail', v)} />
                                    <EditField label="Award Criteria" value={form.awardCriteria} onChange={(v) => handleFieldChange('awardCriteria', v)} />
                                </>
                            ) : (
                                <>
                                    <InfoField label="Title" value={tender.title} />
                                    <InfoField label="Buyer" value={tender.buyer_name || tender.buyer} />
                                    <InfoField label="Source" value={tender.source_platform || tender.source} />
                                    <InfoField label="Nation" value={tender.nation} />
                                    <InfoField label="Region" value={tender.region} />
                                    <InfoField label="Notice Type" value={tender.noticeType || tender.notice_type} />
                                    <InfoField label="Estimated Value" value={formatValue(tender.estimatedValue || tender.estimated_value, tender.currency)} />
                                    <InfoField label="Value Band" value={tender.value_band || tender.valueBand} />
                                    <InfoField label="Published" value={formatDate(tender.published_date || tender.publishedDate)} />
                                    <InfoField label="Deadline" value={formatDate(tender.closing_date || tender.deadline)} />
                                    <InfoField label="Start Date" value={formatDate(tender.startDate || tender.start_date)} />
                                    <InfoField label="Notice ID" value={tender.noticeId || tender.notice_id} mono />
                                </>
                            )}
                        </div>
                    </section>

                    {/* Description */}
                    <section className="card-admin">
                        <div className="card-header">
                            <h2 className="text-sm font-bold text-admin-heading uppercase tracking-wide">Description</h2>
                        </div>
                        <div className="card-body">
                            {editing ? (
                                <div className="space-y-3">
                                    <div>
                                        <label className="label-admin">Short Description</label>
                                        <textarea
                                            value={form.description}
                                            onChange={(e) => handleFieldChange('description', e.target.value)}
                                            rows={4}
                                            className="input-admin w-full resize-y text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="label-admin">Full Description</label>
                                        <textarea
                                            value={form.fullDescription}
                                            onChange={(e) => handleFieldChange('fullDescription', e.target.value)}
                                            rows={8}
                                            className="input-admin w-full resize-y text-sm"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {(tender.description || tender.fullDescription || tender.full_description) ? (
                                        <>
                                             {tender.description && (
                                                <div>
                                                    <p className="text-[10px] font-bold text-admin-muted uppercase tracking-wider mb-2">Summary</p>
                                                    <p className="text-sm text-admin-text leading-relaxed whitespace-pre-wrap">{tender.description}</p>
                                                </div>
                                            )}
                                            
                                        </>
                                    ) : (
                                        <p className="text-sm text-admin-muted font-bold text-center py-4">No description available.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>
                </div>

                {/* Right Column — Sidebar */}
                <div className="space-y-6">
                    {/* Links & Contact */}
                    <section className="card-admin">
                        <div className="card-header">
                            <h2 className="text-sm font-bold text-admin-heading uppercase tracking-wide">Links & Contact</h2>
                        </div>
                        <div className="card-body space-y-3">
                            <InfoField
                                label="Notice URL"
                                value={
                                    (tender.noticeUrl || tender.notice_url) ? (
                                        <a
                                            href={tender.noticeUrl || tender.notice_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-accent-primary hover:underline break-all text-xs"
                                        >
                                            {tender.noticeUrl || tender.notice_url}
                                        </a>
                                    ) : null
                                }
                            />
                            <InfoField label="Contact Email" value={tender.contactEmail || tender.contact_email} />
                            <InfoField label="Award Criteria" value={tender.awardCriteria || tender.award_criteria} />
                            <InfoField label="Document URLs" value={tender.documentUrls || tender.document_urls} mono />
                        </div>
                    </section>

                    {/* Metadata */}
                    <section className="card-admin">
                        <div className="card-header">
                            <h2 className="text-sm font-bold text-admin-heading uppercase tracking-wide">System Metadata</h2>
                        </div>
                        <div className="card-body space-y-3">
                            <InfoField label="Internal ID" value={tender.id} mono />
                            <InfoField label="External Ref" value={tender.external_ref_id} mono />
                            <InfoField label="Notice ID" value={tender.noticeId || tender.notice_id} mono />
                            <InfoField label="Buyer ID" value={tender.buyerId || tender.buyer_id} mono />
                            <InfoField label="Created" value={formatDateTime(tender.created_at)} />
                            <InfoField label="Updated" value={formatDateTime(tender.updated_at)} />
                        </div>
                    </section>

                    {/* Danger Zone */}
                    <section className="card-admin border-status-error/30">
                        <div className="card-header">
                            <h2 className="text-sm font-bold text-status-error uppercase tracking-wide">Danger Zone</h2>
                        </div>
                        <div className="card-body space-y-3">
                            <p className="text-xs text-admin-muted leading-relaxed">
                                Permanently delete this tender from the database. This will also remove all organisation interactions
                                (saves, follows, decisions) for this tender. <strong>This action cannot be undone.</strong>
                            </p>
                            {!showDeleteConfirm ? (
                                <button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    className="w-full py-2.5 px-4 bg-status-error/10 border border-status-error/30 text-status-error text-sm font-bold rounded-lg hover:bg-status-error/20 transition-colors"
                                >
                                    Delete Tender
                                </button>
                            ) : (
                                <div className="space-y-3 p-3 bg-status-error-bg border border-status-error/20 rounded-lg">
                                    <p className="text-xs font-bold text-status-error">
                                        Are you sure? This will permanently delete this tender and all related data.
                                    </p>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={handleDelete}
                                            disabled={deleting}
                                            className="flex-1 py-2 px-4 bg-status-error text-white text-sm font-bold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                                        >
                                            {deleting ? 'Deleting...' : 'Yes, Delete'}
                                        </button>
                                        <button
                                            onClick={() => setShowDeleteConfirm(false)}
                                            className="flex-1 btn-secondary py-2"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    )
}

/* ─── Sub-components ─── */

function MetricCard({ label, value, highlight }) {
    return (
        <div className="card-admin p-5">
            <p className="text-xs font-bold text-admin-muted uppercase tracking-wider">{label}</p>
            <p className={`text-xl font-bold mt-2 tracking-tight capitalize ${highlight ? 'text-status-success' : 'text-brand-navy'}`}>
                {value || '—'}
            </p>
        </div>
    )
}

function InfoField({ label, value, mono = false }) {
    return (
        <div className="bg-gray-50 border border-admin-border rounded-lg p-3 min-w-0">
            <p className="text-[10px] font-bold text-admin-muted uppercase tracking-wider mb-1">{label}</p>
            {typeof value === 'string' || typeof value === 'number' ? (
                <p className={`text-sm font-bold text-admin-text break-words ${mono ? 'font-mono text-xs' : ''}`}>
                    {value || '—'}
                </p>
            ) : (
                value || <p className="text-sm font-bold text-admin-text">—</p>
            )}
        </div>
    )
}

function EditField({ label, value, onChange, type = 'text', span2 = false }) {
    return (
        <div className={span2 ? 'md:col-span-2' : ''}>
            <label className="label-admin">{label}</label>
            <input
                type={type}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                className="input-admin w-full text-sm"
            />
        </div>
    )
}

function TenderStatusBadge({ status }) {
    const styles = {
        open: 'badge-success',
        closed: 'badge-neutral',
        expired: 'badge-error',
    }
    return (
        <span className={`badge ${styles[status] || 'badge-neutral'}`}>
            {status}
        </span>
    )
}

function formatValue(val, currency = 'GBP') {
    if (!val || val === 'Not specified') return '—'
    const num = parseFloat(val)
    if (isNaN(num)) return val
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(num)
}
